import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Pencil, Send, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getPublicCompany, createApplication, getDraft, saveDraft, submitApplication, resumeMyDraft, saveContactProfile,
  duplicateCheck, duplicateOverride,
  EMAIL_RE, validPhone, errMsg,
} from "@/lib/applyApi";
import { DuplicateNotice, regDupMsg } from "@/components/apply/DuplicateNotice";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/brand/AppHeader";
import { StepContact, StepHorse, validateContact, validateHorse } from "@/components/apply/Steps";
import { SubmitterFields, OwnerPermission, validateSubmitter } from "@/components/apply/SubmitterFields";
import { HorseTrainerFields, validateTrainer } from "@/components/apply/TrainerFields";
import { disclosureRows } from "@/components/apply/HealthFields";
import { MediaStep } from "@/components/apply/MediaStep";
import { UploadIndicator } from "@/components/video/UploadIndicator";
import { activeUploads } from "@/lib/uploadManager";
import { scrollToFirstError } from "@/lib/scrollToError";
import { genderLabel, breedLabel, disciplineLabels, priceLabel } from "@/lib/horseOptions";
import { locationLabel, prefLabel, addressLabel, registrationLabel } from "@/lib/geo";

const STEPS = ["Contact", "Horse", "Photos & Videos", "Review"];
// LIVE unless the TEST workflow is explicitly chosen with ?env=test (server only honours it for admins / TEST accounts).
const testFlow = () => new URLSearchParams(window.location.search).get("env") === "test";
const storeKey = (slug) => `apply:${slug}${testFlow() ? ":test" : ""}`;
const PROFILE_KEYS = ["full_name", "email", "phone", "street_address", "city", "postal_code", "country", "region"];
const loginUrl = (next, email) => `/login?next=${encodeURIComponent(next)}${email ? `&email=${encodeURIComponent(email)}` : ""}`;

function Summary({ title, rows, onEdit, testId }) {
  return (
    <section data-testid={testId} className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-stone-900">{title}</h3>
        <button type="button" onClick={onEdit} data-testid={`${testId}-edit`} className="min-h-[44px] px-2 inline-flex items-center gap-1 text-sm font-semibold text-amber-800">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <dl className="grid grid-cols-1 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[130px_1fr] gap-2">
            <dt className="text-stone-500">{k}</dt>
            <dd className="text-stone-900 break-words whitespace-pre-wrap">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Success({ company, onDashboard }) {
  return (
    <div data-testid="apply-success" className="text-center py-10">
      <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
      <h2 className="mt-4 font-display text-2xl font-extrabold text-stone-900">Application submitted</h2>
      <p className="mt-3 text-stone-700 max-w-md mx-auto">
        Your horse application has been submitted to {company.name}. We will review your information and contact you regarding the next steps.
      </p>
      <Button data-testid="apply-go-dashboard" onClick={onDashboard}
        className="mt-7 h-12 px-7 rounded-full bg-amber-800 hover:bg-amber-900 text-white text-base">
        <LayoutDashboard className="h-4 w-4 mr-2" /> Go to Seller Dashboard
      </Button>
    </div>
  );
}

export default function Apply() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const seller = user && user.role === "seller" ? user : null;
  const [savedContact, setSavedContact] = useState(null);
  const [promoExisting, setPromoExisting] = useState(null);
  const [saveProfile, setSaveProfile] = useState(false);
  const [company, setCompany] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(0);
  const [cred, setCred] = useState(null);
  const [app, setApp] = useState({ contact: {}, horse: { breed: "aqha" }, media: [] });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState({ privacy: false, contact: false });
  const [honeypot, setHoneypot] = useState("");
  const [done, setDone] = useState(false);
  const [dup, setDup] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const barnName = app.horse?.horse_name;
  const paperKey = (app.media || []).filter((m) => m.kind === "registration_paper").map((m) => m.id).sort().join(",");
  // Only the CURRENT registration counts: once the papers it was read from are removed/replaced, the old name/number are ignored.
  const regOk = app.horse?.registered === "yes" && (!app.horse.registration_confirmed_papers || app.horse.registration_confirmed_papers === paperKey);
  const regName = regOk ? app.horse.registered_name : "";
  const regNum = regOk ? app.horse.registered_number : "";
  const regKey = `${regName}|${regNum}|${paperKey}`;
  const dupSeq = useRef(0);
  const prevRegKey = useRef(null);
  const dupRef = useRef(null);
  const resetP = useRef(null);
  useEffect(() => { dupRef.current = dup; }, [dup]);
  // Each check gets a sequence number; a slower, older response can never overwrite a newer one.
  const checkDup = async () => {
    const my = ++dupSeq.current;
    setDupChecking(true);
    try {
      await resetP.current?.catch(() => {});  // an override reset for changed papers must land before re-checking
      const d = cred && (regName || regNum || barnName) ? await duplicateCheck(cred.id, cred.token, regName, barnName, regNum) : null;
      if (my === dupSeq.current) setDup(d);
      return d;
    } finally { if (my === dupSeq.current) setDupChecking(false); }
  };
  // Papers or confirmed registration changed: drop the old warning and override at once, then re-check.
  useEffect(() => {
    const changed = prevRegKey.current !== null && prevRegKey.current !== regKey;
    prevRegKey.current = regKey;
    if (!changed) return;
    dupSeq.current++;
    if (dupRef.current?.override && cred) resetP.current = duplicateOverride(cred.id, cred.token, false);
    setDup(null);
  }, [regKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Live duplicate check against this seller's own horses while names are entered (debounced).
  useEffect(() => {
    if (!cred || step !== 1 || !(regName || regNum || barnName)) { dupSeq.current++; setDup(null); setDupChecking(false); return undefined; }
    setDupChecking(true);
    const t = setTimeout(() => { checkDup().catch(() => setDupChecking(false)); }, 450);
    return () => clearTimeout(t);
  }, [cred, step, regKey, barnName]); // eslint-disable-line react-hooks/exhaustive-deps
  const setOverride = (on) => duplicateOverride(cred.id, cred.token, on, regName, regNum).then(() => {
    setDup((d) => ({ ...d, override: on })); setErr(null);
    if (on) persist({ horse: app.horse }).catch(() => {});  // save Step 2 answers the duplicate block held back
  })
    .catch(() => setErr("Could not save your choice — please check your connection and try again."));
  const blocked = (d) => (d?.registered || d?.barn) && !d.override;
  const heading = useRef(null);
  const firstStep = useRef(true);
  // Dashboard "Continue Application" (?resume=1): reopen at the first step that still needs input; nothing is cleared.
  const resumeAt = (a) => {
    if (!new URLSearchParams(window.location.search).get("resume")) return;
    const ce = validateContact(a.contact || {}, EMAIL_RE, validPhone);
    const he = Object.keys(ce).length ? {} : validateHorse(a.horse || {}, a);
    const s = Object.keys(ce).length ? 0 : Object.keys(he).length ? 1 : (a.media || []).some((m) => m.kind === "photo") ? 3 : 2;
    setStep(s);
    const e = s === 0 ? ce : s === 1 ? he : {};
    if (Object.keys(e).length && (s > 0 || a.horse?.horse_name)) {
      setErrors(e);
      setErr("Welcome back — please complete the highlighted fields to continue. Everything you entered is saved.");
      setTimeout(() => scrollToFirstError(document.querySelector("main")), 300);
    }
    window.history.replaceState(null, "", window.location.pathname + (testFlow() ? "?env=test" : ""));
  };

  useEffect(() => {
    if (firstStep.current) { firstStep.current = false; return; }
    heading.current?.focus({ preventScroll: true });
  }, [step]);
  const [loadTry, setLoadTry] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = (n) => getPublicCompany(slug).then((c) => alive && setCompany(c)).catch((x) => {
      if (!alive) return;
      if (x?.response?.status === 404) { setErr("This application page isn't available."); return; }
      if (n < 4) { setTimeout(() => load(n + 1), 1500 * (n + 1)); return; }
      setErr("retry");
    });
    setErr(null);
    load(0);
    return () => { alive = false; };
  }, [slug, loadTry]);
  useEffect(() => {
    if (user === null) return; // auth still loading
    // Admin "Test Application" shortcut: adopt the TEST draft handed over in the URL, then drop it from the address bar.
    const sp = new URLSearchParams(window.location.search);
    if (testFlow() && sp.get("draft") && sp.get("t")) {
      localStorage.setItem(storeKey(slug), JSON.stringify({ id: sp.get("draft"), token: sp.get("t") }));
      window.history.replaceState(null, "", `${window.location.pathname}?env=test`);
    }
    const saved = JSON.parse(localStorage.getItem(storeKey(slug)) || "null");
    if (seller) {
      // Signed-in seller: the draft lives on the account, so it resumes on any device.
      resumeMyDraft(slug, saved, testFlow())
        .then((r) => {
          const c = { id: r.id, token: r.draft_token, owner: seller.id };
          localStorage.setItem(storeKey(slug), JSON.stringify(c));
          setCred(c);
          setApp(r.application);
          setSavedContact(r.saved_contact || null);
          setPromoExisting(r.promo_existing || null);
          resumeAt(r.application);
        })
        .catch((x) => setErr(errMsg(x, "Could not load your saved application.")));
      return;
    }
    if (saved?.owner) {
      // Account-linked draft and nobody is signed in (logged out / session ended): never show it to this visitor.
      localStorage.removeItem(storeKey(slug));
      return;
    }
    if (saved?.id) {
      getDraft(saved.id, saved.token)
        .then((a) => {
          if (a.status !== "draft" || (a.is_test && !testFlow())) { localStorage.removeItem(storeKey(slug)); return; }
          setCred(saved);
          setApp(a);
          resumeAt(a);
        })
        .catch(() => localStorage.removeItem(storeKey(slug)));
    }
  }, [slug, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (body) => {
    let c = cred;
    if (!c) {
      const res = await createApplication(slug, honeypot, testFlow());
      c = { id: res.id, token: res.draft_token };
      localStorage.setItem(storeKey(slug), JSON.stringify(c));
      setCred(c);
    }
    const a = await saveDraft(c.id, c.token, body);
    setApp((cur) => ({ ...a, media: cur.media?.length ? cur.media : a.media }));
  };

  const next = async () => {
    if (step === 0 && !app.submitter?.role) {
      setErr("Please choose Owner / Seller or Trainer / Agent to continue.");
      document.querySelector("[data-testid='apply-submitter']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const e = step === 0 ? { ...validateContact(app.contact, EMAIL_RE, validPhone), ...validateSubmitter(app.submitter || {}, EMAIL_RE, validPhone, app.contact?.email),
      ...(app.submitter?.role === "owner" ? validateTrainer(app.horse || {}, validPhone, app.contact?.email || "") : {}) } : step === 1 ? validateHorse(app.horse, app) : {};
    setErrors(e);
    if (Object.keys(e).length) {
      setErr(`Please fix the highlighted ${Object.keys(e).length === 1 ? "field" : "fields"} before continuing.`);
      scrollToFirstError(document.querySelector("main"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (step === 1) {
        const d = await checkDup();
        if (blocked(d)) {
          if (!d.registered) await persist({ horse: app.horse }).catch(() => {});  // keep what they typed
          setErr("This horse already exists. Go to your Ad Template, or tick Override to submit a new application.");
          requestAnimationFrame(() => document.querySelector('[data-testid^="dup-"]')?.scrollIntoView({ behavior: "smooth", block: "center" }));
          return;
        }
      }
      if (step === 0) await persist({ contact: app.contact, submitter: app.submitter,
        // The submitting trainer is this horse's trainer (same existing trainer fields).
        ...(app.submitter?.role === "trainer" ? { horse: { ...app.horse, has_trainer: "yes", trainer_is_seller: false, trainer_access: "no", trainer_name: app.submitter.trainer_name, trainer_phone: app.submitter.trainer_phone, trainer_email: app.submitter.trainer_email } } : { horse: app.horse }) });
      if (step === 0 && seller && saveProfile && profileDiffers && app.submitter?.role !== "trainer") {
        setSavedContact((await saveContactProfile(app.contact)).saved_contact);
        setSaveProfile(false);
      }
      if (step === 1) await persist({ horse: app.horse });
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (x) {
      if (x?.response?.data?.detail?.code === "duplicate_registered") { setErr(regDupMsg()); checkDup().catch(() => {}); return; }
      setErr(errMsg(x, "Could not save — please check your connection and try again."));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const active = activeUploads(cred.id);
    if (active.length) {
      setErr(`Please wait — still uploading: ${active.map((x) => `${x.name} (${x.progress || 0}%)`).join(", ")}. You can also remove it in Photos & Videos. Your answers are saved.`);
      return;
    }
    if (app.submitter?.role === "trainer" && !app.submitter.owner_permission) {
      setErr("Please confirm you have permission from the owner to submit this horse for sale.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const d = await checkDup();
      if (blocked(d)) {
        goto(1);
        setErr("This horse already exists. Go to your Ad Template, or tick Override to submit a new application.");
        return;
      }
      await persist({ contact: app.contact, submitter: app.submitter || { role: "owner" } });
      await submitApplication(cred.id, cred.token, { consent_privacy: consent.privacy, consent_contact: consent.contact, website: honeypot });
      localStorage.removeItem(storeKey(slug));
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (x) {
      if (x?.response?.data?.detail?.code === "duplicate_registered") {
        goto(1);
        setErr(regDupMsg());
        return;
      }
      setErr(errMsg(x, "Could not submit — please try again."));
      requestAnimationFrame(() => document.querySelector('[data-testid="apply-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }));
    } finally {
      setBusy(false);
    }
  };

  if (!company) {
    return (
      <div data-testid="apply-load-state" className="min-h-screen flex flex-col items-center justify-center gap-3 text-stone-500 px-6 text-center">
        {err === "retry" ? (
          <>
            <p>We couldn't load the application just now. Please try again.</p>
            <button data-testid="apply-load-retry" onClick={() => setLoadTry((t) => t + 1)} className="rounded-full bg-stone-900 text-white px-5 py-2 text-sm font-semibold">Try again</button>
          </>
        ) : err || <Loader2 className="h-6 w-6 animate-spin" />}
      </div>
    );
  }

  const c = app.contact || {};
  const sub = app.submitter || {};
  const h = app.horse || {};
  const photos = (app.media || []).filter((m) => m.kind === "photo");
  const goto = (i) => { setStep(i); setErr(null); window.scrollTo({ top: 0 }); };
  const profileDiffers = !!seller && PROFILE_KEYS.some((k) => (c[k] || "") !== ((savedContact || {})[k] || ""));
  const signIn = async () => {
    setBusy(true);
    const tr = sub.role === "trainer";
    if (PROFILE_KEYS.some((k) => c[k]) || tr) await persist({ contact: c, ...(sub.role ? { submitter: sub } : {}) }).catch(() => {});
    const who = tr ? sub.trainer_email : c.email;
    navigate(loginUrl(`/apply/${slug}${testFlow() ? "?env=test" : ""}`, EMAIL_RE.test(who || "") ? who : ""));
  };
  const toDashboard = () => navigate(seller ? "/dashboard" : loginUrl("/dashboard", c.email));

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-28 overflow-x-clip">
      <header className="on-dark border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <BrandLogo className="h-8 sm:h-10 w-auto mb-4" />
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/70">{company.name}</div>
          <h1 data-testid="apply-title" className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">Sell Your Horse</h1>
          <p className="text-sm text-stone-500 mt-1">Tell us about your horse. It takes about 5 minutes.</p>
          {app?.is_test && <p data-testid="apply-test-badge" className="mt-2 inline-block rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">TEST application</p>}
          {app && testFlow() && !app.is_test && <p data-testid="apply-test-not-allowed" className="mt-2 text-xs font-medium text-amber-800">TEST applications require an admin or TEST account sign-in — this one is LIVE.</p>}
          {seller && !done && (
            <p data-testid="apply-signed-in" className="mt-2 text-xs text-stone-500">
              Signed in as <span className="font-semibold">{seller.email}</span> — your progress is saved to your account and you can continue on any device.
            </p>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-5 pb-32">
        {done ? <Success company={company} onDashboard={toDashboard} /> : (
          <>
            <ol data-testid="apply-steps" aria-label="Application progress" className="grid grid-cols-4 gap-1.5 mb-5">
              {STEPS.map((s, i) => (
                <li key={s} className="text-center" aria-current={i === step ? "step" : undefined}>
                  <div className={`h-1.5 rounded-full ${i <= step ? "bg-amber-800" : "bg-stone-200"}`} />
                  <div className={`mt-1.5 text-[11px] sm:text-xs font-semibold ${i === step ? "text-amber-900" : "text-stone-400"}`}>{s}</div>
                </li>
              ))}
            </ol>
            <h2 ref={heading} tabIndex={-1} data-testid="apply-step-heading" className="font-display text-xl font-bold text-stone-900 mb-4 rounded">Step {step + 1} — {STEPS[step]}</h2>

            <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
              name="website" className="absolute -left-[9999px] h-0 w-0 opacity-0" />

            {step === 0 && !user && (
              <p data-testid="apply-signin-prompt" className="-mt-2 mb-4 text-sm text-stone-600">
                Already have an account?{" "}
                <button type="button" data-testid="apply-signin-link" onClick={signIn} disabled={busy} className="font-semibold text-amber-800 underline underline-offset-2">Sign in</button>
              </p>
            )}
            {step === 0 && <SubmitterFields s={sub} errors={errors} onPick={() => setErr(null)} set={(v) => setApp((a) => {
              const prev = a.submitter || {};
              // A signed-in trainer's prefilled details belong in the trainer fields, not the owner's.
              const mine = v.role === "trainer" && prev.role !== "trainer" && user && (a.contact?.email || "").toLowerCase() === (user.email || "").toLowerCase();
              if (mine) return { ...a, submitter: { ...v, trainer_name: a.contact.full_name, trainer_email: a.contact.email, trainer_phone: a.contact.phone, moved_from_account: true },
                contact: { ...a.contact, full_name: "", email: "", phone: "" } };
              // Switching back to Owner: put the account's own details back if they were moved and nothing new was typed.
              const back = v.role === "owner" && prev.role === "trainer" && prev.moved_from_account && !a.contact?.full_name && !a.contact?.email && !a.contact?.phone;
              if (back) return { ...a, submitter: { ...v, moved_from_account: false }, contact: { ...a.contact, full_name: v.trainer_name, email: v.trainer_email, phone: v.trainer_phone } };
              return { ...a, submitter: v };
            })} />}
            {step === 0 && sub.role && (
              <div key={sub.role} data-testid={`apply-contact-section-${sub.role}`} className="animate-in fade-in slide-in-from-top-2 duration-300">
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">{sub.role === "trainer" ? "Owner's details (the seller who signs the contract)" : "Your contact information"}</p>
                <StepContact owner={sub.role === "trainer"} contact={c} set={(v) => setApp((a) => ({ ...a, contact: v }))} errors={errors} promoExisting={promoExisting} />
              </div>
            )}
            {step === 0 && sub.role === "owner" && (
              <div data-testid="apply-owner-trainer" className="mt-6 rounded-xl border border-stone-200 bg-white/60 p-4 animate-in fade-in duration-300">
                <HorseTrainerFields ownerInvite horse={h} set={(v) => setApp((a) => ({ ...a, horse: v }))} contact={c} errors={errors} />
              </div>
            )}
            {step === 0 && profileDiffers && sub.role === "owner" && (
              <label data-testid="apply-save-profile" className="mt-4 flex items-start gap-3 min-h-[44px] cursor-pointer rounded-xl border border-stone-200 bg-white p-3">
                <Checkbox data-testid="apply-save-profile-checkbox" checked={saveProfile} onCheckedChange={(v) => setSaveProfile(!!v)} className="mt-0.5 h-5 w-5" />
                <span className="text-sm text-stone-700">
                  Save these contact details to my account for future applications.
                  <span className="block text-xs text-stone-500 mt-0.5">This does not change your sign-in email ({seller.email}).</span>
                </span>
              </label>
            )}
            {step === 1 && <StepHorse horse={h} set={(v) => setApp((a) => ({ ...a, horse: v }))} errors={errors}
              app={app} token={cred?.token} company={company} contact={c} onMediaChange={(fn) => setApp((a) => ({ ...a, media: fn(a.media || []) }))}
              dupNotice={<DuplicateNotice dup={dup} onOverride={setOverride} />}
              saveHorse={(v) => persist({ horse: v }).catch((x) => setErr(x?.response?.data?.detail?.code === "duplicate_registered" ? regDupMsg() : errMsg(x, "Could not save — please check your connection and try again.")))} />}
            {step === 2 && cred && (
              <MediaStep app={app} token={cred.token} company={company}
                onMediaChange={(fn) => setApp((a) => ({ ...a, media: fn(a.media || []) }))} />
            )}
            {step === 3 && (
              <div className="space-y-4">
                <Summary testId="review-contact" title={sub.role === "trainer" ? "Owner (Seller) Information" : "Contact Information"} onEdit={() => goto(0)} rows={[
                  ...(sub.role === "trainer" ? [["Submitted by", `${sub.trainer_name} (trainer/agent) · ${sub.trainer_email} · ${sub.trainer_phone}`],
                    ["Contact about this horse", { owner: "Owner", trainer: "Trainer", both: "Both" }[sub.contact_target || "both"]]] : []),
                  ["Full Name", c.full_name], ["Email", c.email], ["Phone", c.phone], ["Mailing Address", addressLabel(c)],
                  ["Contact Preference", prefLabel(c.contact_preference)],
                  ["Promotional Messages", (c.promo_consent_method === "user" ? c.promo_consent : promoExisting?.subscribed) ? "Yes" : "No"],
                ]} />
                <Summary testId="review-horse" title="Horse Information" onEdit={() => goto(1)} rows={[
                  ["Horse's Barn Name", h.horse_name], ["Registration", registrationLabel(h)], ["Age", h.age], ["Gender", genderLabel(h.gender)], ["Breed", breedLabel(h)], ["Discipline", disciplineLabels(h).join(", ")],
                  ["Trainer", (h.has_trainer || (h.trainer_name ? "yes" : "")) === "no" ? "No trainer" : h.trainer_is_seller ? `${h.trainer_name} (you)` : h.trainer_name],
                  ["Trainer's Phone Number", h.has_trainer === "no" ? "" : h.trainer_phone], ["Trainer's Email", h.has_trainer === "no" ? "" : h.trainer_email],
                  ...(sub.role === "owner" && h.has_trainer === "yes" && !h.trainer_is_seller ? [["Trainer Access", h.trainer_access === "yes" ? "Yes — invitation sent when you submit" : "No — contact details only"]] : []),
                  ["Description", h.description], ...disclosureRows(h), ["Expected Price", priceLabel(h)], ["Location", locationLabel(h)],
                ]} />
                <Summary testId="review-media" title="Photos & Videos" onEdit={() => goto(2)} rows={[
                  ["Photos", `${photos.length} uploaded`],
                  ...(app.horse?.registered === "yes" ? [["Registration Papers", `${(app.media || []).filter((m) => m.kind === "registration_paper").length} uploaded`]] : []),
                  ["Videos", "Shown in Photos & Videos"],
                ]} />
                <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
                  <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                    <Checkbox data-testid="consent-privacy" checked={consent.privacy} onCheckedChange={(v) => setConsent((s) => ({ ...s, privacy: !!v }))} className="mt-0.5 h-5 w-5" />
                    <span className="text-sm text-stone-700">
                      I have read and understand the{" "}
                      <a data-testid="privacy-policy-link" href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-amber-800 underline font-semibold">Privacy Policy</a>.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                    <Checkbox data-testid="consent-contact" checked={consent.contact} onCheckedChange={(v) => setConsent((s) => ({ ...s, contact: !!v }))} className="mt-0.5 h-5 w-5" />
                    <span className="text-sm text-stone-700">I give {company.name} permission to contact me about this application by email or phone.</span>
                  </label>
                  <OwnerPermission s={sub} set={(v) => setApp((a) => ({ ...a, submitter: v }))} />
                </div>
              </div>
            )}
            {err && <p data-testid="apply-error" role="status" className="mt-4 text-sm font-medium text-red-600">{err}</p>}
          </>
        )}
      </main>

      {!done && (
        <div data-testid="apply-nav" className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200 pb-[env(safe-area-inset-bottom)]">
          {cred && <UploadIndicator scopeId={cred.id} onOpen={() => { setStep(2); setTimeout(() => document.getElementById("video-uploader")?.scrollIntoView({ behavior: "smooth" }), 150); }} />}
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <Button data-testid="apply-back" variant="ghost" disabled={step === 0 || busy} onClick={() => goto(step - 1)} className="h-12 px-4 text-stone-600">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            {step < 3 ? (
              <Button data-testid="apply-next" onClick={step === 2 ? () => goto(3) : next} disabled={busy || (step === 1 && dupChecking)}
                className="h-12 flex-1 max-w-[220px] rounded-full bg-amber-800 hover:bg-amber-900 text-white text-base">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Next <ArrowRight className="h-4 w-4 ml-1.5" /></>}
              </Button>
            ) : (
              <Button data-testid="apply-submit" onClick={submit} disabled={busy || !consent.privacy || !consent.contact}
                className="h-12 flex-1 max-w-[260px] rounded-full bg-amber-800 hover:bg-amber-900 text-white text-base">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit Application</>}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
